import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import InterestedPartiesDialogEntry from './InterestedPartiesDialogEntry';
import { saveInterestedPartyEdit, getCurrentUserId, getFirestoreTimestamp } from './utils/interestedPartiesFirestore';
import { useMSGReadOnly } from '../../contexts/ReadOnlyContext';
import { firebaseImport } from '@/js/external-js/config/main_config';
import { useTenancy } from '@/contexts/TenancyContext';
import { db } from '@/js/external-js/config/main_config';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import EditableField from '@/components/ui/EditableField';
import { FrameworkBadge } from '@/app/pages/msg/components/FrameworkBadge';

import {
  getStandardName,
  fetchRelatedDocuments,
  populateStandardsList,
  populateCustomContent,
  fetchPageContent,
  fetchPageContentMDX, getAllModules, fetchInterestedParties
} from '@/app/pages/msg/utils/common';
import { useMarkdownComponents } from '@/app/pages/msg/components/MDXFields';
import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { Toggle } from '@/components/ui/toggle';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Eye, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { Timestamp } from 'firebase/firestore';
import RelatedClausesAccordianItem from '@/app/pages/msg/components/RelatedClauses';
import AdditionalContent from '../../components/AdditionalContent';
import FootNote from '../../components/FootNote';
import SupportingDocs from '../../components/SupportingDocs';
import { useMSGVersion } from '../../hooks/useMSGVersion';
import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';
import { cleanStandardName } from './InternalAndExternalIssues';

export default function InterestedPartiesSection() {
  const { activeTenancy, userIdToName } = useTenancy();
  const { incrementVersion } = useMSGVersion();
  const { isReadOnly } = useMSGReadOnly();

  // MDX Things
  const [pageTemplateMDX, setPageTemplateMDX] = React.useState<string>('');
  const [modulesList, setModulesList] = useState([]);
  const clauseStandards = useRef<Set<string>>(new Set());
  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());

  // Function to update pageClauses
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };


  const [pageTemplate, setPageTemplate] = React.useState<string>('');
  const [supportingDocs, setSupportingDocs] = React.useState([]);
  const [sortedFrameworks, setSortedFrameworks] = React.useState([]);
  const [dynamicStandardNames, setDynamicStandardNames] = useState<Map<string, string>>(new Map());
  const [dynamicContent, setDynamicContent] = React.useState<string>("");

  const pageName = 'context';
  const contentPath = ['interestedParties'];
  const pageRoute = '/MSG/context/interestedParties';

  interface Framework {
    id?: string;
    standard: string;
    controlID?: string;
    controlName?: string;
    controlDescription?: string;
    framework?: string;
    controlApplicable?: boolean;
  }

  interface Party {
    id?: string | number;
    party: string;
    requirement: string;
    requirementsAddressedThroughISMS: string;
    frameworks: Framework[];
    dateAdded?: string | { seconds: number; nanoseconds: number };
    dateUpdated?: string | { seconds: number; nanoseconds: number };
    updatedBy?: string;
  }

  const [interestedParties, setInterestedParties] = React.useState<Party[]>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const toggleRow = (id: number) => {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (activeTenancy.id == "") return;

    const loadModules = async () => {
      const allModules = await getAllModules();
      const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

      allModules.forEach(module => {
        module.visible = storedVisibilities[module.id] ?? false;
      });

      setModulesList(allModules);
    }

    const getPageContentMDX = fetchPageContentMDX(db, pageName, 'interestedParties_MDX', activeTenancy, setPageTemplateMDX);
    const getRelatedDocuments = fetchRelatedDocuments(db, activeTenancy.id, pageRoute, setSupportingDocs);

    // From src/components/relatedControls.component:195
    // const popStandardsList = populateStandardsList(db, pageName, contentPath, setDynamicStandardNames, setSortedFrameworks);

    // New function to populate the contents from the customer
    const getCustomContent = populateCustomContent(db, activeTenancy.id, pageRoute, setDynamicContent);

    const getInterestedPartiesItems = fetchInterestedParties(db, activeTenancy.id, setInterestedParties);


    const load = async () => {
      // await getInterestedPartiesItems();
    };
    load();
    loadModules();
    return () => {
      getPageContentMDX();
      getCustomContent();
      getRelatedDocuments();
      getInterestedPartiesItems()
    };
  }, [activeTenancy]);

  // Dialog state for editing
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEditParty, setSelectedEditParty] = useState<Party | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePartyId, setDeletePartyId] = useState<string | null>(null);

  // Deep-link: open party edit dialog when /msg/context/parties/:id
  const { id: routePartyId } = useParams<{ id?: string }>();
  useEffect(() => {
    const rid = String(routePartyId || '').trim();
    if (!rid || !Array.isArray(interestedParties) || interestedParties.length === 0) return;
    const match = interestedParties.find(p => String(p.id) === rid);
    if (match) {
      setSelectedEditParty(match);
      setDialogOpen(true);
    }
  }, [routePartyId, interestedParties]);

  const onActions = {
    onEdit: (row: Party) => {
      setSelectedEditParty(row);
      setDialogOpen(true);
    },
    onAdd: () => {
      setSelectedEditParty(null);
      setDialogOpen(true);
    }
  };

  const handleSave = async (party: Party) => {
    const isNew = !party.id;
    const companyID = activeTenancy.id;
    // Capture old data for version control
    const oldParty = isNew ? null : interestedParties.find(p => p.id === party.id);
    // Generate unique id for new party
    const newId = isNew ? `${Date.now()}-${Math.floor(Math.random() * 10000)}` : party.id.toString();
    let firestoreDateAdded = party.dateAdded;
    if (firestoreDateAdded) {
      if (typeof firestoreDateAdded === 'string') {
        firestoreDateAdded = getFirestoreTimestamp(firestoreDateAdded);
      } else if (typeof firestoreDateAdded === 'object' && 'seconds' in firestoreDateAdded && 'nanoseconds' in firestoreDateAdded) {
        firestoreDateAdded = firebaseImport.firestore.Timestamp.fromMillis(
          firestoreDateAdded.seconds * 1000 + Math.floor(firestoreDateAdded.nanoseconds / 1e6)
        );
      } else {
        firestoreDateAdded = getFirestoreTimestamp(new Date());
      }
    } else {
      firestoreDateAdded = getFirestoreTimestamp(new Date());
    }
    const firestoreParty = {
      ...party,
      id: newId,
      dateAdded: firestoreDateAdded,
      dateUpdated: getFirestoreTimestamp(new Date()),
      updatedBy: getCurrentUserId(),
      frameworks: Array.isArray(party.frameworks)
        ? party.frameworks.map(f => typeof f === 'string' ? f : f.standard || '')
        : [],
    };
    await saveInterestedPartyEdit(companyID, newId, firestoreParty);

    // Increment version with snapshot
    try {
      await incrementVersion(
        isNew ? 'interested_party_create' : 'interested_party_update',
        isNew ? `Created interested party: ${party.name}` : `Updated interested party: ${party.name}`,
        {
          partyId: newId,
          oldData: oldParty,
          newData: firestoreParty
        }
      );
    } catch (versionError) {
      console.error('Error updating version:', versionError);
    }

    setInterestedParties(prev => {
      const idx = prev.findIndex(p => p.id === newId);
      if (idx !== -1) {
        // Edit existing
        const updated = [...prev];
        updated[idx] = {
          ...party,
          ...firestoreParty,
          frameworks: Array.isArray(party.frameworks)
            ? party.frameworks
            : (firestoreParty.frameworks || []).map(f => ({ standard: f })),
        };
        return updated;
      } else {
        // Add new
        return [
          ...prev,
          {
            ...party,
            ...firestoreParty,
            frameworks: Array.isArray(party.frameworks)
              ? party.frameworks
              : (firestoreParty.frameworks || []).map(f => ({ standard: f })),
          }
        ];
      }
    });
    setDialogOpen(false);
    setSelectedEditParty(null);
  };

  const sortParties = (parties: any[]) => {
    return [...parties].sort((a, b) => {
      // Handle cases where party might be undefined
      const partyA = (a.party || '').toLowerCase();
      const partyB = (b.party || '').toLowerCase();
      return partyA.localeCompare(partyB);
    });
  };


  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm relative">
        <div className="absolute right-4 top-4 z-10">
          <div className="flex items-center space-x-2">
            <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
            <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
          </div>
        </div>
        <div className="px-6 py-6">
          <MarkdownRenderer source={pageTemplateMDX} components={useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses)} />
        </div>
        <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
          <AdditionalContent pageRoute={pageRoute} />
          <FootNote />
        </div>
      </div>
      <div className='bg-card rounded-xl border border-border shadow-sm px-6 py-6'>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Interested Parties</h3>
          {!isReadOnly && (
            <Button onClick={onActions.onAdd} size="sm" className="flex items-center space-x-1">
              <Plus className="h-4 w-4" />
              <span>Add New Party</span>
            </Button>
          )}
        </div>
        <Table className="w-full table">
          <TableHeader>
            <TableRow>
              <TableHead>Party</TableHead>
              <TableHead>Party Requirement</TableHead>
              <TableHead>Outcome on Management System</TableHead>
              <TableHead>Frameworks</TableHead>
              <TableHead className="text-right">Manage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortParties(interestedParties).map(row => (
              <React.Fragment key={row.id}>
                <TableRow onClick={() => toggleRow(row.id)}>
                  <TableCell>{row.party}</TableCell>
                  <TableCell className="whitespace-normal break-words max-w-[200px]">{row.requirement}</TableCell>
                  <TableCell className="whitespace-normal break-words max-w-[200px]"><p>{row.requirementsAddressedThroughISMS}</p></TableCell>
                  <TableCell>
                    {row.frameworks?.length > 0
                      ? row.frameworks.map(f => {
                        return <Badge key={f.standard || f} variant="secondary">{cleanStandardName(f.standard || f)}</Badge>;
                      })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={isReadOnly ? "View" : "Edit"}
                      onClick={() => onActions.onEdit(row)}
                    >
                      {isReadOnly ? <Eye className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                    </Button>
                    {/* <Button
                      onClick={e => { e.stopPropagation(); toggleRow(row.id); }}
                      variant="ghost"
                    >
                      {expandedRows.includes(row.id) ? '▼' : '▶'}
                    </Button> */}
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={e => {
                          e.stopPropagation();
                          setDeletePartyId(row.id);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {/* {expandedRows.includes(row.id) && (
                  <TableRow>
                    <TableCell colSpan={5} className="bg-muted text-muted-foreground p-4">
                      <div className="grid grid-cols-3 grid-rows-2 gap-4 text-sm">
                        <div className="font-semibold">Date Added</div>
                        <div className="font-semibold">Date Updated</div>
                        <div className="font-semibold">Updated By</div>

                        <div>
                          {row.dateAdded instanceof Timestamp
                            ? row.dateAdded.toDate().toLocaleDateString('en-AU')
                            : 'Please Update This Party'}
                        </div>
                        <div>
                          {row.dateUpdated instanceof Timestamp
                            ? row.dateUpdated.toDate().toLocaleDateString('en-AU')
                            : 'Please Update This Party'}
                        </div>
                        <div>{userIdToName[row.updatedBy] ?? 'Deleted or Unknown'}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                )} */}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      {/* <div className='bg-card rounded-xl border border-border shadow-sm overflow-hidden'>
        <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
      </div> */}
      <InterestedPartiesDialogEntry
        open={dialogOpen}
        party={selectedEditParty}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isReadOnly={isReadOnly}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Interested Party</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this interested party? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletePartyId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deletePartyId) {
                  const companyID = activeTenancy.id;
                  await import('@/app/pages/msg/utils/common').then(async mod => {
                    await mod.deleteInterestedParty(db, companyID, deletePartyId);
                  });
                  setInterestedParties(prev => prev.filter(p => p.id !== deletePartyId));
                }
                setDeleteConfirmOpen(false);
                setDeletePartyId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
